import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { NearbyPoint } from "points/models";
import { formatDistance, NEARBY_RADIUS_METERS } from "points/pointValidation";
import pointsService from "services/pointsService";

/**
 * What already exists near the coordinate being placed.
 *
 * This is the reason the screen is worth building. Adding a point is easy; adding the fourth copy
 * of a yard someone else already entered under a slightly different name is easier still, and
 * that is how the table got into the state it is in. So the answer to "does this already exist?"
 * is put in front of the user while they are still typing, not held back until they press save.
 *
 * Two modes:
 *  - advisory: shown as soon as a coordinate resolves. Informational; the user reads it and
 *    carries on.
 *  - blocking: shown after OrderApi answers 409. These are the points that stopped the save, and
 *    the user has to tick the acknowledgement before the form will re-submit with their ids.
 *
 * Nothing here merges, edits or deactivates anything. Every row links out to the existing point
 * so the decision is made on that screen, with its full history, rather than from a summary.
 *
 * The one write it does offer is activation. Inactive points are in this list on purpose -- a
 * deactivated row is still a duplicate, and adding a second copy of it is exactly the outcome the
 * screen exists to stop -- but that left the operator staring at the location they wanted, greyed
 * out, with no way forward. So an inactive row gets an Activate action. Refreshing the list
 * afterwards is the parent's job: it owns the search, this panel only reports what happened.
 */

const hubSummary = (point: NearbyPoint): string => {
  const hubs = point.hubNames ?? [];
  if (hubs.length === 0) return "No hubs assigned";
  return hubs.join(", ");
};

const addressSummary = (point: NearbyPoint): string => {
  const parts = [point.addressLine1, point.city, point.stateAbbreviation, point.zipCode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "No address on record";
};

const NearbyPointRow = ({
  point,
  onActivated,
}: {
  point: NearbyPoint;
  onActivated?: (clientPointId: number) => void;
}) => {
  const [isActivating, setIsActivating] = React.useState(false);
  const [isActivated, setIsActivated] = React.useState(false);
  const [activationError, setActivationError] = React.useState<string>();

  // Per row, not per panel: two inactive neighbours can be worked one after the other, and a
  // failure on one says nothing about the other.
  const activate = async () => {
    setIsActivating(true);
    setActivationError(undefined);

    const outcome = await pointsService.activatePoint(point.clientPointId, point.clientId);

    if (outcome.status === "activated") {
      // Marked here as well as reported upwards. The parent is expected to re-run the nearby
      // search, which unmounts this row -- but the panel must still read correctly if it does
      // not, rather than leaving a button that has already done its work.
      setIsActivated(true);
      setIsActivating(false);
      onActivated?.(point.clientPointId);
      return;
    }

    // OrderApi's own message, not a generic one. It is the only thing that says WHY: the
    // railroads whose points come from a feed are refused, and so is a point belonging to
    // another client.
    setActivationError(outcome.message);
    setIsActivating(false);
  };

  const showActivate = !point.isActive && !isActivated;

  return (
    <Box sx={{ py: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: "wrap" }}>
        <MuiLink
          component={Link}
          // ClientPointID -- see AddPoint. The points screens resolve a point by ClientPointID,
          // and NearbyPoint is one of the few payloads carrying the real geo.Point id as well.
          to={`/points/${point.clientPointId}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.35 }}
        >
          {point.clientPointName || `Point ${point.clientPointId}`}
          <OpenInNewIcon sx={{ fontSize: 13 }} />
        </MuiLink>

        {point.clientPointAltName1 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            (also {point.clientPointAltName1})
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        <Chip size="small" label={formatDistance(point.distanceMeters)} sx={{ fontWeight: 600 }} />
        {!point.isActive && !isActivated && (
          <Chip size="small" color="default" variant="outlined" label="Inactive" />
        )}
        {isActivated && <Chip size="small" color="success" variant="outlined" label="Activated" />}
      </Stack>

      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {addressSummary(point)}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
        <Chip size="small" variant="outlined" label={point.clientName || `Client ${point.clientId}`} />
        {/* A client point carries a point of service per hub it serves, so this is routinely more
            than one. Named rather than counted -- "3 hubs" does not tell anyone whether the hub
            they care about is already covered. */}
        <Chip size="small" variant="outlined" icon={<PlaceIcon />} label={hubSummary(point)} />

        {showActivate && (
          <Button
            size="small"
            variant="outlined"
            disabled={isActivating}
            onClick={activate}
            startIcon={isActivating ? <CircularProgress size={12} color="inherit" /> : undefined}
          >
            {isActivating ? "Activating…" : "Activate"}
          </Button>
        )}
      </Stack>

      {isActivated && (
        <Typography variant="caption" sx={{ color: "success.main" }}>
          This location is active again. Use it instead of adding a new one.
        </Typography>
      )}

      {activationError && (
        <Alert severity="error" sx={{ mt: 0.75 }}>
          {activationError}
        </Alert>
      )}
    </Box>
  );
};

const NearbyPointsPanel = ({
  nearby,
  isLoading,
  error,
  hasCoordinate,
  isBlocking = false,
  isAcknowledged = false,
  onAcknowledgedChange,
  onActivated,
}: {
  nearby: NearbyPoint[];
  isLoading: boolean;
  error?: string;
  /** False before an address is geocoded or a coordinate typed -- there is nothing to ask yet. */
  hasCoordinate: boolean;
  /** True once these points have come back inside a 409 and are stopping the save. */
  isBlocking?: boolean;
  isAcknowledged?: boolean;
  onAcknowledgedChange?: (acknowledged: boolean) => void;
  /**
   * An inactive neighbour was reactivated. The parent owns the nearby search, so it re-runs it --
   * this panel does not refresh itself, or the two would disagree about what is on screen.
   */
  onActivated?: (clientPointId: number) => void;
}) => {
  if (!hasCoordinate) {
    return (
      <Alert severity="info">
        Search an address or enter a latitude and longitude. Anything already recorded within{" "}
        {NEARBY_RADIUS_METERS} m of it will be listed here before you can save.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          Checking what already exists here…
        </Typography>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={54} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error} Until this check succeeds there is no way to tell whether this location is already
        recorded, so save with care.
      </Alert>
    );
  }

  if (nearby.length === 0) {
    return (
      <Alert severity="success">
        Nothing is recorded within {NEARBY_RADIUS_METERS} m of this coordinate. This looks like a
        new location.
      </Alert>
    );
  }

  return (
    <Box>
      <Alert severity={isBlocking ? "error" : "warning"} sx={{ mb: 1.5 }}>
        {isBlocking ? (
          <>
            <b>Not saved.</b> {nearby.length} existing {nearby.length === 1 ? "location is" : "locations are"}{" "}
            close enough that this may be a duplicate. Open each one and check before continuing.
          </>
        ) : (
          <>
            <b>
              {nearby.length} existing {nearby.length === 1 ? "location" : "locations"}
            </b>{" "}
            already within {NEARBY_RADIUS_METERS} m. Check whether one of them is the place you are
            about to add.
          </>
        )}
      </Alert>

      <Box sx={{ maxHeight: 320, overflowY: "auto", pr: 1 }}>
        {nearby.map((point, index) => (
          <React.Fragment key={point.clientPointId}>
            {index > 0 && <Divider />}
            {/* Both modes render through here, so Activate is offered on an advisory list and on
                the blocking 409 list alike -- the 409 case is the one where the operator is most
                likely to be looking at the deactivated row they actually wanted. */}
            <NearbyPointRow point={point} onActivated={onActivated} />
          </React.Fragment>
        ))}
      </Box>

      {isBlocking && (
        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAcknowledged}
                onChange={(e) => onAcknowledgedChange?.(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I have looked at {nearby.length === 1 ? "this location" : "these locations"} and the
                one I am adding is a different place.
              </Typography>
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default NearbyPointsPanel;
